(() => {
  const desktopQuery = window.matchMedia('(min-width: 1024px)');
  const stageSelector = '[data-momento-desktop-product-stage]';
  const pdpFollowupSelector = '[data-momento-pdp-desktop-followup]';

  if (!customElements.get('momento-desktop-inventory-banner')) {
    class MomentoDesktopInventoryBanner extends HTMLElement {
      connectedCallback() {
        if (this.abortController) return;

        this.abortController = new AbortController();
        const { signal } = this.abortController;
        const scheduleUpdate = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => this.updatePosition());
        };

        window.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
        window.addEventListener('resize', scheduleUpdate, { signal });
        desktopQuery.addEventListener('change', scheduleUpdate, { signal });
        this.updatePosition();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = 0;
      }

      updatePosition() {
        this.frame = 0;
        const isDesktop = desktopQuery.matches;
        const height = this.getBoundingClientRect().height;
        const distance = isDesktop
          ? Math.min(Math.max(window.scrollY, 0), height)
          : 0;
        const isHidden = isDesktop && window.scrollY >= height;
        this.style.setProperty('--momento-desktop-inventory-shift', `${-distance}px`);
        this.toggleAttribute('data-scroll-hidden', isHidden);
        this.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
      }
    }

    customElements.define('momento-desktop-inventory-banner', MomentoDesktopInventoryBanner);
  }

  if (!customElements.get('momento-desktop-shop-menu')) {
    class MomentoDesktopShopMenu extends HTMLElement {
      connectedCallback() {
        if (this.abortController) return;

        this.trigger = this.querySelector('[data-desktop-shop-trigger]');
        this.panel = this.querySelector('[data-desktop-shop-panel]');
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.addEventListener('pointerenter', () => this.open(), { signal });
        this.addEventListener('pointerleave', () => this.scheduleClose(), { signal });
        this.addEventListener('focusin', () => this.open(), { signal });
        this.addEventListener('focusout', () => {
          requestAnimationFrame(() => {
            if (!this.contains(document.activeElement)) this.close();
          });
        }, { signal });
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.hasAttribute('data-open')) return;
          event.preventDefault();
          this.close();
          this.trigger?.focus();
        }, { signal });
        this.querySelectorAll('.desktop-shop-menu__panel a').forEach((link) => {
          link.addEventListener('click', () => this.close(), { signal });
        });
        desktopQuery.addEventListener('change', () => this.close(), { signal });
      }

      disconnectedCallback() {
        window.clearTimeout(this.closeTimer);
        this.abortController?.abort();
        this.abortController = null;
      }

      open() {
        if (!desktopQuery.matches || !this.trigger || !this.panel) return;
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
        this.setAttribute('data-open', '');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.panel.setAttribute('aria-hidden', 'false');
      }

      scheduleClose() {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(() => this.close(), 140);
      }

      close() {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
        this.removeAttribute('data-open');
        this.trigger?.setAttribute('aria-expanded', 'false');
        this.panel?.setAttribute('aria-hidden', 'true');
      }
    }

    customElements.define('momento-desktop-shop-menu', MomentoDesktopShopMenu);
  }

  const findProductSections = () => {
    const mainContent = document.getElementById('MainContent');
    const productSection = mainContent?.querySelector(':scope > .momento-product-main-section')
      || mainContent?.querySelector(':scope > .momento-product-showcase-section');
    const bundles = productSection?.nextElementSibling;
    const productInfo = bundles?.nextElementSibling;

    if (
      !productSection ||
      !bundles?.classList.contains('momento-product-bundles-section') ||
      !productInfo?.classList.contains('momento-product-info-section')
    ) {
      return null;
    }

    return { mainContent, productSection, bundles, productInfo };
  };

  const restorePdpFollowup = () => {
    const followup = document.querySelector(pdpFollowupSelector);
    if (!followup) return;

    const productInfo = document.querySelector('.momento-product-info--pdp');
    const accordionList = productInfo?.querySelector('.momento-accordion-list');
    const review = followup.querySelector('.momento-review-preview');
    const ribbons = followup.querySelector('.momento-pdp-mobile-after-review');

    if (productInfo && accordionList && review && ribbons) {
      productInfo.insertBefore(review, accordionList);
      productInfo.insertBefore(ribbons, accordionList);
    }

    followup.remove();
  };

  const mountPdpFollowup = (stage) => {
    const productInfo = stage?.querySelector('.momento-product-info--pdp');
    if (!productInfo || !stage.querySelector('.momento-product-pdp')) return;

    const review = productInfo.querySelector('.momento-review-preview');
    const ribbons = productInfo.querySelector('.momento-pdp-mobile-after-review');
    if (!review || !ribbons) return;

    let followup = document.querySelector(pdpFollowupSelector);
    if (!followup) {
      followup = document.createElement('div');
      followup.className = 'momento-pdp-desktop-after-stage';
      followup.setAttribute('data-momento-pdp-desktop-followup', '');
    }

    stage.after(followup);
    followup.append(review, ribbons);
  };

  const unwrapProductStage = () => {
    restorePdpFollowup();
    const stage = document.querySelector(stageSelector);
    if (!stage?.parentElement) return;

    const description = stage.querySelector('[data-info-accordion]');
    if (description?.hasAttribute('data-desktop-auto-opened') && description.classList.contains('open')) {
      description.querySelector('[data-accordion-trigger]')?.click();
    }

    const parent = stage.parentElement;
    [...stage.children].forEach((section) => parent.insertBefore(section, stage));
    stage.remove();
  };

  const openDesktopDescription = (stage) => {
    if (!stage?.querySelector('.momento-product-pdp')) return;

    const description = stage?.querySelector('[data-info-accordion]');
    const trigger = description?.querySelector('[data-accordion-trigger]');
    if (!description || !trigger || description.classList.contains('open')) return;

    description.setAttribute('data-desktop-auto-opened', '');
    trigger.click();
  };

  const updateProductStage = () => {
    if (!desktopQuery.matches) {
      unwrapProductStage();
      return;
    }

    const currentStage = document.querySelector(stageSelector);
    if (currentStage) {
      openDesktopDescription(currentStage);
      mountPdpFollowup(currentStage);
      return;
    }
    const sections = findProductSections();
    if (!sections) return;

    const stage = document.createElement('div');
    stage.className = 'momento-product-desktop-stage';
    stage.setAttribute('data-momento-desktop-product-stage', '');
    sections.productSection.before(stage);
    stage.append(sections.productSection, sections.bundles, sections.productInfo);
    openDesktopDescription(stage);
    mountPdpFollowup(stage);
  };

  updateProductStage();
  desktopQuery.addEventListener('change', updateProductStage);
  document.addEventListener('shopify:section:load', updateProductStage);
  document.addEventListener('shopify:section:unload', updateProductStage);
})();
